import {
  JOB_TYPE_LABELS,
  isDeclinableJobType,
  type DeclinableJobType,
  type JobType,
} from '@lib/enums/job-type.enum'
import { UNIDENTIFIED_LABEL } from '@lib/enums/tree-mark.enum'
import type {
  IrrigationRepairRow,
  ProposalDocumentDto,
  TreeScheduleRow,
} from '@lib/dto/proposal-document.dto'
import { parseMoney } from '@lib/helpers/money'
import {
  jobTypeReach,
  priceSelection,
  repairDropped,
  selectionDropsTree,
  speciesKey,
  treeChoiceFor,
  type ProposalSelection,
} from '@lib/helpers/proposal-selection'

/**
 * The things a client can leave out, each with how far it reaches and what it
 * is worth. Shared by the adjust panel and the tests, so the reach a control
 * states is the reach it has.
 *
 * Every figure is measured against what the client has chosen so far, not
 * against the whole proposal. Once an area is left out, a problem is counted
 * and priced on the trees still included, so unticking it moves the total by
 * the figure beside it.
 */

export interface ScheduleTree {
  site: string
  row: TreeScheduleRow
}

/** Every tree, in schedule order. */
export function scheduleTrees(document: ProposalDocumentDto): ScheduleTree[] {
  return document.blocks.flatMap((block) =>
    block.type === 'treeSchedule'
      ? block.rows.map((row) => ({ site: block.siteName, row }))
      : [],
  )
}

export interface ScheduleRepair {
  site: string
  repair: IrrigationRepairRow
}

/** Every irrigation repair, site by site. */
export function scheduleRepairs(
  document: ProposalDocumentDto,
): ScheduleRepair[] {
  return document.blocks.flatMap((block) =>
    block.type === 'irrigationSchedule'
      ? block.repairs.map((repair) => ({ site: block.siteName, repair }))
      : [],
  )
}

/** Whether a tree is still bought under a selection: in on the plan, priced. */
export function isTreeIncluded(
  site: string,
  row: TreeScheduleRow,
  selection: ProposalSelection,
): boolean {
  return (
    !selectionDropsTree(site, row, selection) &&
    treeChoiceFor(site, row, selection) !== 'NONE'
  )
}

/**
 * Whether a tree is still pruned. A removal replaces the pruning, and takes
 * its jobs and its problems' treatments with it, so neither can be left out
 * of a tree coming down.
 */
function isTreePruned(
  site: string,
  row: TreeScheduleRow,
  selection: ProposalSelection,
): boolean {
  return (
    !selectionDropsTree(site, row, selection) &&
    treeChoiceFor(site, row, selection) === 'PRUNE'
  )
}

/** One thing to leave out. */
export interface Choice<K> {
  key: K
  label: string
  /**
   * How much of what is still included it reaches: "14 trees", or "6 of 9
   * trees" when it cannot reach all of them.
   *
   * Null when other choices have already left out everything it covers, so
   * the box would change nothing.
   */
  reach: string | null
  /**
   * What the box is worth with everything else as chosen, in cents before
   * tax: what unticking it takes off, or what ticking it again puts back.
   */
  cents: number
}

function trees(count: number): string {
  return `${String(count)} ${count === 1 ? 'tree' : 'trees'}`
}

function repairs(count: number): string {
  return `${String(count)} ${count === 1 ? 'repair' : 'repairs'}`
}

/** "12 trees", "3 repairs", "12 trees · 3 repairs", or null for neither. */
function treesAndRepairs(treeCount: number, repairCount: number): string | null {
  if (treeCount === 0 && repairCount === 0) return null
  if (repairCount === 0) return trees(treeCount)
  if (treeCount === 0) return repairs(repairCount)
  return `${trees(treeCount)} · ${repairs(repairCount)}`
}

function pretax(document: ProposalDocumentDto, selection: ProposalSelection): number {
  return parseMoney(priceSelection(document, selection).totalAmount) ?? 0
}

/**
 * The total with a box ticked, less the total with it unticked, everything
 * else as chosen. Whichever way the box is now, that is what it moves.
 */
function worth(
  document: ProposalDocumentDto,
  ticked: ProposalSelection,
  unticked: ProposalSelection,
): number {
  return pretax(document, ticked) - pretax(document, unticked)
}

export function siteChoices(
  document: ProposalDocumentDto,
  selection: ProposalSelection,
): Choice<string>[] {
  const all = scheduleTrees(document)
  const allRepairs = scheduleRepairs(document)
  const sites = [
    ...new Set([
      ...all.map((tree) => tree.site),
      ...allRepairs.map((repair) => repair.site),
    ]),
  ]
  return sites.map((site) => {
    const ticked = {
      ...selection,
      excludedSites: selection.excludedSites.filter((entry) => entry !== site),
    }
    const unticked = { ...ticked, excludedSites: [...ticked.excludedSites, site] }
    return {
      key: site,
      label: site,
      reach: treesAndRepairs(
        all.filter(
          (tree) => tree.site === site && isTreeIncluded(tree.site, tree.row, ticked),
        ).length,
        allRepairs.filter(
          (entry) =>
            entry.site === site &&
            !repairDropped(document, entry.site, entry.repair, ticked),
        ).length,
      ),
      cents: worth(document, ticked, unticked),
    }
  })
}

/** Areas: every area name on a site, so two rings called "Pool" go together. */
export function areaChoices(
  document: ProposalDocumentDto,
  selection: ProposalSelection,
): Choice<{ site: string; name: string }>[] {
  const all = scheduleTrees(document)
  const allRepairs = scheduleRepairs(document)
  // A repair is in its timer's area, so an area reaches its repairs too.
  const found: { site: string; name: string }[] = []
  const add = (site: string, name: string | null | undefined) => {
    if (name == null || name === '') return
    const known = found.some(
      (candidate) => candidate.site === site && candidate.name === name,
    )
    if (!known) found.push({ site, name })
  }
  for (const tree of all) add(tree.site, tree.row.areaName)
  for (const entry of allRepairs) add(entry.site, entry.repair.areaName)
  const sites = new Set(found.map((entry) => entry.site))
  return found.map((area) => {
    const isArea = (entry: { site: string; name: string }) =>
      entry.site === area.site && entry.name === area.name
    const ticked = {
      ...selection,
      excludedAreaNames: selection.excludedAreaNames.filter(
        (entry) => !isArea(entry),
      ),
    }
    const unticked = {
      ...ticked,
      excludedAreaNames: [...ticked.excludedAreaNames, area],
    }
    return {
      key: area,
      label: sites.size > 1 ? `${area.name} · ${area.site}` : area.name,
      reach: treesAndRepairs(
        all.filter(
          (tree) =>
            tree.site === area.site &&
            tree.row.areaName === area.name &&
            isTreeIncluded(tree.site, tree.row, ticked),
        ).length,
        allRepairs.filter(
          (entry) =>
            entry.site === area.site &&
            entry.repair.areaName === area.name &&
            !repairDropped(document, entry.site, entry.repair, ticked),
        ).length,
      ),
      cents: worth(document, ticked, unticked),
    }
  })
}

export function speciesChoices(
  document: ProposalDocumentDto,
  selection: ProposalSelection,
): Choice<string>[] {
  const all = scheduleTrees(document)
  // Ordered by the whole proposal, so the rows hold still as boxes change.
  const counts = new Map<string, { label: string; count: number }>()
  for (const tree of all) {
    const key = speciesKey(tree.row.species)
    const entry = counts.get(key)
    counts.set(key, {
      label: tree.row.species?.name ?? UNIDENTIFIED_LABEL,
      count: (entry?.count ?? 0) + 1,
    })
  }
  return [...counts.entries()]
    .sort(([, a], [, b]) => b.count - a.count)
    .map(([key, { label }]) => {
      const ticked = {
        ...selection,
        excludedSpecies: selection.excludedSpecies.filter((entry) => entry !== key),
      }
      const unticked = { ...ticked, excludedSpecies: [...ticked.excludedSpecies, key] }
      const count = all.filter(
        (tree) =>
          speciesKey(tree.row.species) === key &&
          isTreeIncluded(tree.site, tree.row, ticked),
      ).length
      return {
        key,
        label,
        reach: count === 0 ? null : trees(count),
        cents: worth(document, ticked, unticked),
      }
    })
}

/**
 * Kinds of work, where declining one takes something off.
 *
 * Only on trees whose time was split across their jobs; elsewhere there is no
 * figure to take away, so the reach says "6 of 9 trees" rather than letting a
 * total fall by less than somebody expected with nothing saying why. Access
 * and setup never appear: the crew cannot be on the property without them.
 */
export function jobChoices(
  document: ProposalDocumentDto,
  selection: ProposalSelection,
): Choice<DeclinableJobType>[] {
  const all = scheduleTrees(document)
  const types = new Set<JobType>()
  for (const tree of all) {
    for (const job of tree.row.jobs) types.add(job.type)
  }
  const choices: Choice<DeclinableJobType>[] = []
  for (const type of types) {
    if (!isDeclinableJobType(type)) continue
    if (jobTypeReach(document, type).declinable === 0) continue
    const ticked = {
      ...selection,
      excludedJobTypes: selection.excludedJobTypes.filter((entry) => entry !== type),
    }
    const unticked = { ...ticked, excludedJobTypes: [...ticked.excludedJobTypes, type] }
    let total = 0
    let declinable = 0
    for (const tree of all) {
      const job = tree.row.jobs.find((candidate) => candidate.type === type)
      if (job === undefined || !isTreePruned(tree.site, tree.row, ticked)) continue
      total += 1
      if (job.minutes !== null) declinable += 1
    }
    let reach: string | null = null
    if (total > 0) {
      reach =
        declinable === total
          ? trees(total)
          : `${String(declinable)} of ${trees(total)}`
    }
    choices.push({
      key: type,
      label: JOB_TYPE_LABELS[type],
      reach,
      cents: worth(document, ticked, unticked),
    })
  }
  return choices
}

export function problemChoices(
  document: ProposalDocumentDto,
  selection: ProposalSelection,
): Choice<string>[] {
  const all = scheduleTrees(document)
  // Ordered by the whole proposal, so the rows hold still as boxes change.
  const counts = new Map<string, number>()
  for (const tree of all) {
    for (const problem of tree.row.problems) {
      counts.set(problem.name, (counts.get(problem.name) ?? 0) + 1)
    }
  }
  return [...counts.entries()]
    .sort(([, a], [, b]) => b - a)
    .map(([name]) => {
      const ticked = {
        ...selection,
        excludedProblems: selection.excludedProblems.filter((entry) => entry !== name),
      }
      const unticked = { ...ticked, excludedProblems: [...ticked.excludedProblems, name] }
      const count = all.filter(
        (tree) =>
          tree.row.problems.some((problem) => problem.name === name) &&
          isTreePruned(tree.site, tree.row, ticked),
      ).length
      return {
        key: name,
        label: name,
        reach: count === 0 ? null : trees(count),
        cents: worth(document, ticked, unticked),
      }
    })
}

/**
 * Kinds of irrigation repair, most common first, by how many there are: "14"
 * bad nozzles is fourteen heads, however many repairs record them.
 */
export function irrigationKindChoices(
  document: ProposalDocumentDto,
  selection: ProposalSelection,
): Choice<string>[] {
  const allRepairs = scheduleRepairs(document)
  // Ordered by the whole proposal, so the rows hold still as boxes change.
  const counts = new Map<string, number>()
  for (const { repair } of allRepairs) {
    counts.set(repair.kind, (counts.get(repair.kind) ?? 0) + repair.count)
  }
  return [...counts.entries()]
    .sort(([, a], [, b]) => b - a)
    .map(([kind]) => {
      const ticked = {
        ...selection,
        excludedIrrigationKinds: selection.excludedIrrigationKinds.filter(
          (entry) => entry !== kind,
        ),
      }
      const unticked = {
        ...ticked,
        excludedIrrigationKinds: [...ticked.excludedIrrigationKinds, kind],
      }
      const kept = allRepairs.filter(
        (entry) =>
          entry.repair.kind === kind &&
          !repairDropped(document, entry.site, entry.repair, ticked),
      )
      const count = kept.reduce((sum, entry) => sum + entry.repair.count, 0)
      return {
        key: kind,
        label: kind,
        reach: kept.length === 0 ? null : String(count),
        cents: worth(document, ticked, unticked),
      }
    })
}

/** Zones with repairs in them, by the survey's name, site by site. */
export function zoneChoices(
  document: ProposalDocumentDto,
  selection: ProposalSelection,
): Choice<{ site: string; zone: string }>[] {
  const allRepairs = scheduleRepairs(document)
  const found: { site: string; zone: string }[] = []
  for (const { site, repair } of allRepairs) {
    if (repair.zone === null) continue
    const known = found.some(
      (candidate) => candidate.site === site && candidate.zone === repair.zone,
    )
    if (!known) found.push({ site, zone: repair.zone })
  }
  const sites = new Set(found.map((entry) => entry.site))
  return found.map((zone) => {
    const isZone = (entry: { site: string; zone: string }) =>
      entry.site === zone.site && entry.zone === zone.zone
    const ticked = {
      ...selection,
      excludedZones: selection.excludedZones.filter((entry) => !isZone(entry)),
    }
    const unticked = { ...ticked, excludedZones: [...ticked.excludedZones, zone] }
    const count = allRepairs.filter(
      (entry) =>
        entry.site === zone.site &&
        entry.repair.zone === zone.zone &&
        !repairDropped(document, entry.site, entry.repair, ticked),
    ).length
    return {
      key: zone,
      label: sites.size > 1 ? `${zone.zone} · ${zone.site}` : zone.zone,
      reach: count === 0 ? null : repairs(count),
      cents: worth(document, ticked, unticked),
    }
  })
}
